'use no memo';

/* eslint-disable import/prefer-default-export, complexity */
import { isEmpty } from 'lodash-es';
import {
  addMinutes,
  format,
  isToday,
  isTomorrow,
  differenceInDays,
} from 'date-fns';

import { OrderAhead } from '@local/api';

import { formatTime, formatDate } from 'helpers/format';

const ORDER_WHEN_OPTIONS = {
  later: {
    label: 'Order Ahead',
    // Indicates the order is for later but user needs to pick date & time
    value: undefined,
  },
  now: {
    label: 'ASAP',
    // The CN API represents orders for now by passing `null` for `when`
    value: null,
  },
};

export const MINUTES_PER_HOUR = 60;

const API_DATE_STR_REGEX = /(\d{4})-?(\d{2})-?(\d{2})(\d{2})?(\d{2})?/;
/**
 * Generates a JavaScript Date object from a CN API Date/Time string. If no
 * time is provided, the returned date will be for the start of the day.
 */
export function getDateForApiDateStr(dateStr: string | undefined): Date {
  const result = API_DATE_STR_REGEX.exec(dateStr || '');

  if (result && result.length >= 4) {
    const [, year, month, date, hour, minute] = result;

    if (hour && minute) {
      return new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(date, 10),
        parseInt(hour, 10),
        parseInt(minute, 10)
      );
    } else {
      return new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(date, 10)
      );
    }
  }

  // note -- intentionally return invalid date
  return new Date('');
}

/**
 * Returns a string that describes the next available order day
 */
export function displayNextAvailableDay(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'EEE, MMM d'); // Mon, Jan 1
}

/**
 * Returns a string that describes the next available open time
 */
export function getNextAvailableDisplayText({
  nextAvailableDateString,
  showDateTimeOnly = false,
}: {
  nextAvailableDateString: string;
  showDateTimeOnly: boolean;
}): string {
  const nextAvailableDate = getDateForApiDateStr(nextAvailableDateString);

  return `${showDateTimeOnly ? '' : 'Opens '}${displayNextAvailableDay(
    nextAvailableDate
  )} at ${format(nextAvailableDate, 'h:mm a')}`;
}

/**
 * Returns a string that describes the restaurant open time
 */
interface FulfillmentDetailsType {
  display_hours: {
    dow: string;
    ranges?: { to: string }[];
  }[];
  is_available_now: boolean;
  next_available_time: string;
}

export function getOpenTime(restaurant: {
  fulfillment: {
    delivery?: FulfillmentDetailsType;
    pickup?: FulfillmentDetailsType;
  };
}): string {
  const { fulfillment } = restaurant;
  const deliveryDisplayHours = fulfillment?.delivery?.display_hours;
  const pickupDisplayHours = fulfillment?.pickup?.display_hours;
  const isDeliveryOnly = deliveryDisplayHours && !pickupDisplayHours;

  const today = format(new Date(), 'EEEE');
  // Do not try to find pickup hours if restaurant is delivery only
  const todaysHours = isDeliveryOnly
    ? deliveryDisplayHours?.find((day) => day.dow === today)
    : pickupDisplayHours?.find((day) => day.dow === today);

  const lastHourOpen =
    todaysHours?.ranges &&
    !isEmpty(todaysHours.ranges) &&
    formatTime(todaysHours.ranges[todaysHours.ranges.length - 1].to);

  const isOpen =
    fulfillment?.pickup?.is_available_now ||
    fulfillment?.delivery?.is_available_now;
  const nextAvailableDateString =
    fulfillment?.pickup?.next_available_time ||
    fulfillment?.delivery?.next_available_time;

  if (!isOpen && nextAvailableDateString) {
    return getNextAvailableDisplayText({
      nextAvailableDateString,
      showDateTimeOnly: false,
    });
  }

  // in the case of menu preview
  if (!lastHourOpen) {
    return 'Restaurant not currently accepting orders';
  }

  return `Open until ${lastHourOpen}`;
}

export function isSameDayOfWeek(dow: string): boolean {
  const day = format(new Date(), 'EEEE');
  return day === dow;
}

// new Date() returns UTC time, so getLocalTime() adjusts for user's timezone
function getLocalTime(): string {
  const timezoneOffset = new Date().getTimezoneOffset();
  const todayInMilliseconds = Date.now();
  const timezoneAdjusted = todayInMilliseconds - timezoneOffset;
  const today = new Date(timezoneAdjusted);

  // Jan is considered 0, so need to add 1 for standard month format
  const month = today.getMonth() + 1;
  const day = today.getDate();
  const year = today.getFullYear();

  // need to adjust to add 0 for months Jan - Sept
  const formattedMonth = month < 10 ? `0${month}` : month;

  // need to adjust to add 0 for days 1 - 9
  const formattedDay = day < 10 ? `0${day}` : day;

  const formattedToday = `${year}${formattedMonth}${formattedDay}`;

  return formattedToday;
}

export function isSameDay(date: string): boolean {
  // ASAP orders are null, so if we receive null for date, it will be same day
  if (!date) return true;

  const today = formatDate(getLocalTime());
  const orderDay = formatDate(date);
  return today === orderDay;
}

/**
 * Determines whether datetime is within 30 days of now
 */
export function isWithin30Days(date: string): boolean {
  const now = new Date();
  const dateString = getDateForApiDateStr(date); // convert date to Date() accepted string
  const orderDate = new Date(dateString);
  return differenceInDays(now, orderDate) < 30;
}

/**
 * Determines whether datetime is within 24 hours of now
 */
export function isWithin24Hours(date: string): boolean {
  const now = new Date();
  const dateString = getDateForApiDateStr(date); // convert date to Date() accepted string
  const orderDate = new Date(dateString);
  const oneDay = 60 * 60 * 24 * 1000; // 24 hours in milliseconds
  return now.valueOf() - orderDate.valueOf() < oneDay;
}

export function parseExpirationDate(expirationDate: string) {
  const expiresAtParsed = getDateForApiDateStr(expirationDate);
  const date = new Date(expiresAtParsed);

  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(date);
}

export function timeOfDay() {
  const hour = new Date().getHours();
  if (hour > 18) {
    return 'Evening';
  } else if (hour >= 12) {
    return 'Afternoon';
  } else {
    return 'Morning';
  }
}

export function getFormattedWhenObject(dateStr: string, precision: number = 0) {
  const formattedWhen = getDateForApiDateStr(dateStr);
  const fulfillmentDate = format(formattedWhen, 'EEE, MMM d'); // Mon, Jan 1
  const fulfillmentTimeStart = format(formattedWhen, 'h:mm a'); // 7:35pm
  const fulfillmentTimeEnd = format(
    addMinutes(formattedWhen, precision),
    'h:mm a'
  );

  return {
    fulfillmentDate,
    fulfillmentTimeStart,
    fulfillmentTimeEnd,
  };
}

export function toTwoDigits(val: number) {
  // Ensure that single digit numbers are prepended by a `0`
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString
  return val.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });
}

export function getBitsTimeSinceMidnight({
  firstAvailableBit,
  precision,
}: {
  firstAvailableBit: number;
  precision: number;
}) {
  const offSetInMilliseconds = firstAvailableBit * precision * 60000;
  const midnight = new Date('December 1, 2001 00:00:00');
  // Add offset in MS to generic midnight Date
  const dateSinceMidnight = new Date(midnight.getTime() + offSetInMilliseconds);

  return {
    hours: toTwoDigits(dateSinceMidnight.getHours()),
    minutes: toTwoDigits(dateSinceMidnight.getMinutes()),
    formatted: {
      HHmm: format(dateSinceMidnight, 'HHmm'),
      'h:mm a': format(dateSinceMidnight, 'h:mm a'),
    },
  };
}

export function formatTimeStamp({
  formatted,
}: {
  formatted: { HHmm: string; 'h:mm a': string };
}) {
  return {
    label: formatted['h:mm a'],
    value: formatted.HHmm,
  };
}

export function getOptionsForSelectedFulfillMethod({
  orderAhead,
  selectedDate,
  fulfillMethod,
}: {
  orderAhead: OrderAhead;
  selectedDate: Date;
  fulfillMethod: 'curbside' | 'pickup' | 'delivery';
}) {
  if (orderAhead && orderAhead.days) {
    const selectedDay = orderAhead.days.find(
      (day) => day.date === format(selectedDate, 'yyyy-MM-dd')
    );
    if (selectedDay) {
      return selectedDay[fulfillMethod].reduce((acc: object[], slot, index) => {
        if (slot) {
          const timeSinceMidnight = getBitsTimeSinceMidnight({
            firstAvailableBit: index,
            precision: orderAhead.precision,
          });
          return acc.concat(formatTimeStamp(timeSinceMidnight));
        }
        return acc;
      }, []);
    }
  }

  return [];
}

export function calculateTimes({
  dateStr,
  isSelectedFulfillmentAvailableNow,
  orderAhead,
  fulfillMethod,
}: {
  dateStr: string;
  isSelectedFulfillmentAvailableNow: boolean;
  orderAhead: OrderAhead;
  fulfillMethod: 'curbside' | 'pickup' | 'delivery';
}) {
  const selectedDate = getDateForApiDateStr(dateStr);

  const formattedOptions = getOptionsForSelectedFulfillMethod({
    orderAhead,
    selectedDate,
    fulfillMethod,
  });

  if (isSelectedFulfillmentAvailableNow && isToday(selectedDate)) {
    return [ORDER_WHEN_OPTIONS.now, ...formattedOptions];
  }

  return formattedOptions;
}
